import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, Divider, Flex, Grid, Image, Paper, Stack, Text } from "@mantine/core";
import { IconDownload } from "@tabler/icons-react";
import { PreviewedVideoPlayer, VideoFile } from "@testuj.to/react";
import { Review } from "src/graphql/generated/graphql";
import { CampaignQueryDataType } from "src/hooks/campaigns";

import { ExportCampaignResultsModal } from "../../../../modals/ExportCampaignResults";
import { LightBox } from "../../../LightBox";

export interface MediaTabProps {
    campaign: CampaignQueryDataType;
    reviews: Review[];
}

// The same button under every picture and video: a link while the file has a
// presigned downloadUrl, a disabled button until it does.
const DownloadButton = ({ url }: { url?: string | null }) => {
    const { t } = useTranslation(["common"]);

    return url ? (
        <Button
            component="a"
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            variant="default"
            radius={8}
            compact
            leftIcon={<IconDownload size="1em" />}
        >
            {t("download_title")}
        </Button>
    ) : (
        <Button variant="default" radius={8} compact disabled leftIcon={<IconDownload size="1em" />}>
            {t("download_title")}
        </Button>
    );
};

export const MediaTab = ({ campaign, reviews }: MediaTabProps) => {
    const { t } = useTranslation(["common"]);

    const [selectedImageIndex, setSelectedImageIndex] = useState<number>(-1);

    const allImages = useMemo(() => {
        return [
            ...(campaign?.images || []).map((file) => file.file),
            ...(reviews || []).flatMap((review) => review?.images),
        ];
    }, [campaign?.images, reviews]);

    const allVideos = useMemo(() => {
        return (reviews || []).flatMap((review) => review?.videos || []);
    }, [reviews]);

    const handleClose = useCallback(() => {
        setSelectedImageIndex(-1);
    }, []);

    return (
        <Paper p={36} radius={12}>
            <Flex mb={24} align="center" justify="space-between" wrap="wrap" gap={10}>
                <Text style={{ whiteSpace: "nowrap" }} weight={600}>
                    {allImages?.length} {t("campaign_images_photos_from_campaign")}
                </Text>
                <Button
                    radius={8}
                    variant="default"
                    onClick={() =>
                        ExportCampaignResultsModal.display({
                            campaignId: campaign.id,
                        })
                    }
                >
                    {t("campaign_export_all_media")}
                </Button>
            </Flex>
            <Grid>
                {(allImages || []).map((image, index) => (
                    <Grid.Col key={`${index}${image?.src}`} md={6} lg={4} xl={3}>
                        <Stack spacing="xs" align="flex-start">
                            <Image
                                key={index}
                                radius={8}
                                height={184}
                                alt="Random image"
                                style={{ cursor: "pointer" }}
                                src={image?.src + "?w=400"}
                                onClick={() => setSelectedImageIndex(index)}
                            />
                            <DownloadButton url={image?.downloadUrl} />
                        </Stack>
                    </Grid.Col>
                ))}
                {selectedImageIndex >= 0 && (
                    <LightBox images={allImages} selectedIndex={selectedImageIndex} onClose={handleClose} />
                )}
            </Grid>
            {allVideos.length > 0 && (
                <>
                    <Divider my={24} color="gray.2" />
                    <Text mb={24} style={{ whiteSpace: "nowrap" }} weight={600}>
                        {t("campaign_media_videos_from_campaign", { count: allVideos.length })}
                    </Text>
                    <Grid>
                        {allVideos.map((video, index) => (
                            <Grid.Col key={`${index}${video?.id}`} md={6} lg={4} xl={3}>
                                {video?.isBeingProcessed ? (
                                    <Text color="dimmed">{t("review_video_processing")}</Text>
                                ) : (
                                    <Stack spacing="xs" align="flex-start">
                                        <Box>
                                            <PreviewedVideoPlayer
                                                video={video as VideoFile}
                                                height={184}
                                                controlSize={16}
                                                progressBarPreviewHeight={50}
                                            />
                                        </Box>
                                        <DownloadButton url={video?.downloadUrl} />
                                    </Stack>
                                )}
                            </Grid.Col>
                        ))}
                    </Grid>
                </>
            )}
        </Paper>
    );
};
